import React, { useEffect, useMemo } from 'react';
import { css } from '@linaria/core';
import { brand, findColorFromCSSVariable, theme } from '@terveystalo/design-system-theme';
import { TextFieldMessage } from './Message';
import { classnames } from '../../utils';
import { TextFieldInput } from './Input';
import { FormFieldLabel } from '../FormFieldLabel';
import { ScreenReaderOnly } from '../../atoms';
import forwardRefAs from 'forward-ref-as';
import { Popover } from '../../atoms/Popover';
import { FormFieldDescription } from '../FormFieldDescription';
const determineTextColor = (disabled) => {
    if (disabled) {
        return findColorFromCSSVariable(brand.textField.color.disabled.content);
    }
    return findColorFromCSSVariable(brand.textField.color.default.content);
};
/**
 * Component for displaying an input or text area field with label and helper/validation messages.
 * All native input and text area element props are supported and the `ref` will be forwarded to
 * the input or text area field to make e.g. focus triggers possible.
 *
 * The TextField can be used either as a controlled or uncontrolled component and it should work nicely
 * with form validation libraries, such as Formik.
 */
export const TextField = forwardRefAs(({ as = 'input', id, label, icon, name, invalid, valid, disabled, invalidMessage, helperMessage, className, required, tooltip, description, ...rest }, ref) => {
    useEffect(() => {
        if (!id && !name) {
            throw new Error('Please provide either an id or a name for the TextField');
        }
    }, [id, name]);
    // Make sure an id is set, the component will throw an error if neither is defined, so we never reach the empty string
    const fieldId = id || name || '';
    const color = determineTextColor(disabled);
    // Create ids for the described-by attribute. This will help the screen readers to
    // read the message elements when the input field is focused
    const describedBy = useMemo(() => {
        const prefix = fieldId;
        const ids = { helper: `${prefix}-helper-msg`, invalid: `${prefix}-invalid-msg`, valid: `${prefix}-valid-msg` };
        return {
            ids,
            attribute: [helperMessage && ids.helper, invalid && invalidMessage && ids.invalid, valid && ids.valid]
                .filter(Boolean)
                .join(' '),
        };
    }, [fieldId, helperMessage, invalid, valid, invalidMessage]);
    return (React.createElement("div", { className: classnames(css `
            display: flex;
            flex-direction: column;
            margin-bottom: ${theme.spacing['1_5']};
          `, className) },
        React.createElement("div", { className: classnames(css `
              display: flex;
              flex-direction: row;
              gap: ${theme.spacing['0_5']};
              margin-bottom: ${theme.spacing['0_5']};
            `) },
            React.createElement(FormFieldLabel, { fieldId: fieldId, label: label, disabled: disabled, required: required }),
            tooltip && (React.createElement(Popover, { id: fieldId, label: tooltip.label, position: tooltip.position, className: tooltip.className }, tooltip.content))),
        description && React.createElement(FormFieldDescription, null, description),
        React.createElement(TextFieldInput, { as: as, id: fieldId, name: name, ref: ref, icon: icon, required: required, "aria-invalid": invalid, invalid: invalid, valid: valid, disabled: disabled, describedByIds: describedBy.attribute, ...rest }),
        React.createElement(TextFieldMessage, { id: describedBy.ids.invalid, color: findColorFromCSSVariable(brand.textField.color.invalid.content) }, invalid && invalidMessage),
        React.createElement(TextFieldMessage, { id: describedBy.ids.helper, color: color }, helperMessage),
        React.createElement(ScreenReaderOnly, { id: describedBy.ids.valid, "aria-live": "polite" }, valid && '✓')));
});


import "../../../css/molecules/TextField/TextField.css";
